import UnitsDropdown from '@components/UnitsDropdown';
import { brand } from '@utility/config';
import { MuteText } from '@elements/Common';
import {
  ControlledTextFormField,
  DropdownFormField,
  InputRight,
  MaskedNumberFormField,
} from '@elements/FormField';
import { CommonUtility } from '@utility/common';
import { tooltipTexts } from '@utility/constants/common';
import { useMemo } from 'react';
import { cn } from '@skupreme/shared/ui';
import * as yup from 'yup';

export const AttributeSchema = {
  alias: yup.string().trim().required('Name is required field'),
  units_per_carton: yup
    .number()
    .typeError('Quantity per carton is required field')
    .moreThan(0, 'Quantity per carton must be greater than 0')
    .required('Quantity per carton is required field')
    .when('$maxUnitPerCarton', (maxUnitPerCarton, schema) =>
      schema.max(
        maxUnitPerCarton || 20000000,
        `Quantity per carton must be less than ${maxUnitPerCarton || 20000000}`,
      ),
    )
    .when('$minUnitPerCarton', (minUnitPerCarton, schema) =>
      schema.min(
        minUnitPerCarton || 0,
        `Quantity per carton must be greater than ${minUnitPerCarton || 0}`,
      ),
    ),
  carton_length: yup
    .number()
    .typeError('Carton length is required field')
    .moreThan(0, 'Carton length must be greater than 0')
    .required('Carton length is required field'),
  carton_width: yup
    .number()
    .typeError('Carton width is required field')
    .moreThan(0, 'Carton width must be greater than 0')
    .required('Carton width is required field'),
  carton_height: yup
    .number()
    .typeError('Carton height is required field')
    .moreThan(0, 'Carton height must be greater than 0')
    .required('Carton height is required field'),
  carton_gross_weight: yup
    .number()
    .typeError('Carton gross weight is required field')
    .moreThan(0, 'Carton gross weight must be greater than 0')
    .required('Carton gross weight is required field'),
  carton_net_weight: yup
    .number()
    .typeError('Carton net weight is required field')
    .moreThan(0, 'Carton net weight must be greater than 0')
    .required('Carton net weight is required field')
    .when('carton_gross_weight', (carton_gross_weight, schema) =>
      schema.test(
        'carton_net_weight',
        'Should be less than Gross Weight',
        carton_net_weight => {
          if (!isNaN(carton_gross_weight) && !isNaN(carton_net_weight)) {
            return carton_net_weight <= carton_gross_weight;
          }
          return true;
        },
      ),
    ),
};

export const AttributeObject = {
  carton_gross_weight: '',
  carton_height: '',
  carton_length: '',
  carton_net_weight: '',
  carton_width: '',
  units_per_carton: '',
  alias: '',
};

/** The unit shown at the end of a measurement field. */
export const UnitLabel = InputRight;

const AttributeType = {
  components: 'components',
  other: 'other',
};

const AttributeText = {
  [AttributeType.components]: {
    aliasLabel: 'Name',
    aliasPlaceholder: 'Carton Configuration Name',
    aliasHelpText: tooltipTexts.aliasName,
  },
  [AttributeType.other]: {
    aliasLabel: 'Carton Alias Name',
    aliasPlaceholder: 'Carton Alias Name',
    aliasHelpText: tooltipTexts.aliasName,
  },
};

export const convertFieldsToMetric = (
  watchFields,
  optionsComponentsMetadata,
) => {
  const item = { ...AttributeObject };
  Object.keys(AttributeObject).forEach(key => {
    if (['units_per_carton'].includes(key)) {
      const updated = CommonUtility.convertOrderUnit(false, {
        unitOfMeasurement: watchFields.unit_of_measurement,
        optionsComponentsMetadata,
        value: watchFields?.[key] ?? 0,
      });

      item[key] = updated > 0 ? Math.round(updated) : 0;
    } else if (['price', 'units_per_carton', 'alias'].includes(key)) {
      item[key] = watchFields[key];
    } else if (['carton_net_weight', 'carton_gross_weight'].includes(key)) {
      item[key] = CommonUtility.poundToKilo(watchFields[key]);
    } else {
      item[key] = CommonUtility.inchToCenti(watchFields[key]);
    }
  });
  return item;
};

export const convertFieldsToImperial = (
  watchFields,
  optionsComponentsMetadata,
) => {
  const item = { ...AttributeObject };
  Object.keys(AttributeObject).forEach(key => {
    if (['units_per_carton'].includes(key)) {
      const updated = CommonUtility.convertOrderUnit(true, {
        unitOfMeasurement: watchFields.unit_of_measurement,
        optionsComponentsMetadata,
        value: watchFields?.[key] ?? 0,
      });

      item[key] = updated > 0 ? Math.round(updated) : 0;
    } else if (['price', 'alias'].includes(key)) {
      item[key] = watchFields[key];
    } else if (['carton_net_weight', 'carton_gross_weight'].includes(key)) {
      item[key] = CommonUtility.kiloToPound(watchFields[key]);
    } else {
      item[key] = CommonUtility.centiToInch(watchFields[key]);
    }
  });
  return item;
};

const decimalLimit = 2;

export function AddEditAttributeFormElement({
  addByTextbox,
  aliasDropdownChanges,
  allowAdditions = true,
  control,
  currentAlias,
  currentUnit,
  dropdownOptions,
  errors,
  imperialForm,
  isDuplicate,
  optionsComponentsMetadata,
  setValue,
  type = AttributeType.other,
  unit = '',
  unitFieldName = 'attributes_unit_of_measurement',
}) {
  const { weightText, heightTooltipText, heightText } = useMemo(
    () => ({
      heightText: imperialForm ? 'inch' : 'cm',
      weightText: imperialForm ? 'lb' : 'kg',
      heightTooltipText: imperialForm ? 'inches' : 'cm',
    }),
    [imperialForm],
  );

  return (
    <>
      {addByTextbox ? (
        <ControlledTextFormField
          error={errors.alias || isDuplicate}
          name="alias"
          label={AttributeText[type].aliasLabel}
          helpText={AttributeText[type].aliasHelpText}
          hint={
            isDuplicate
              ? `This alias already exists. ${brand.name} only allows 1 unique alias per item`
              : errors.alias?.message
          }
          control={control}
          defaultValue=""
          placeholder={AttributeText[type].aliasPlaceholder}
          disabled={currentAlias}
          required
        />
      ) : (
        <DropdownFormField
          error={errors.alias}
          required
          disabled={currentAlias}
          name="alias"
          label={AttributeText[type].aliasLabel}
          helpText={AttributeText[type].aliasHelpText}
          hint={errors.alias?.message}
          control={control}
          defaultValue=""
          options={dropdownOptions}
          placeholder={AttributeText[type].aliasPlaceholder}
          // @ts-expect-error - TS2322: Type '{ error: any; required: true; disabled: any; name: string; label: string; ...
          allowAdditions={allowAdditions}
          dropdownChanged={aliasDropdownChanges}
        />
      )}
      <div className="sk:hidden!">
        <ControlledTextFormField
          control={control}
          label="Unit of Measurement"
          name={unitFieldName}
        />
      </div>
      <div className="sk:flex sk:flex-wrap sk:-mx-4">
        <div className="sk:min-w-0 sk:flex-1 sk:px-4">
          <MaskedNumberFormField
            required
            error={errors.carton_length}
            hint={errors.carton_length?.message}
            label={<MuteText>Carton Length</MuteText>}
            placeholder="Length"
            name="carton_length"
            helpText={`${tooltipTexts.cartonLength} ${heightTooltipText}`}
            control={control}
            defaultValue=""
            maskOptions={{ allowDecimal: true, decimalLimit }}
            inputRight={
              <UnitLabel
                className={cn(errors.carton_length && 'sk:text-danger')}
              >
                {heightText}
              </UnitLabel>
            }
          />
        </div>
        <div className="sk:min-w-0 sk:flex-1 sk:px-4">
          <MaskedNumberFormField
            error={errors.carton_width}
            hint={errors.carton_width?.message}
            label={<MuteText>Carton Width</MuteText>}
            name="carton_width"
            required
            placeholder="Width"
            helpText={`${tooltipTexts.cartonWidth} ${heightTooltipText}`}
            control={control}
            defaultValue=""
            maskOptions={{ allowDecimal: true, decimalLimit }}
            inputRight={
              <UnitLabel
                className={cn(errors.carton_width && 'sk:text-danger')}
              >
                {heightText}
              </UnitLabel>
            }
          />
        </div>
        <div className="sk:min-w-0 sk:flex-1 sk:px-4">
          <MaskedNumberFormField
            error={errors.carton_height}
            hint={errors.carton_height?.message}
            label={<MuteText>Carton Height</MuteText>}
            helpText={`${tooltipTexts.cartonHeight} ${heightTooltipText}`}
            name="carton_height"
            placeholder="Height"
            control={control}
            defaultValue=""
            required
            maskOptions={{ allowDecimal: true, decimalLimit }}
            inputRight={
              <UnitLabel
                className={cn(errors.carton_height && 'sk:text-danger')}
              >
                {heightText}
              </UnitLabel>
            }
          />
        </div>
      </div>
      <div className="sk:flex sk:flex-wrap sk:-mx-4">
        <div className="sk:min-w-0 sk:flex-1 sk:px-4 sk:mb-2!">
          <MaskedNumberFormField
            error={errors.units_per_carton}
            hint={errors.units_per_carton?.message}
            label={<MuteText>Units Per Carton</MuteText>}
            name="units_per_carton"
            placeholder="Quantity"
            control={control}
            required
            helpText={
              currentUnit
                ? `This is the number of ${currentUnit} per carton`
                : ''
            }
            defaultValue=""
            maxValue={20000000}
            fieldClass="sk:mb-0!"
            inputRight={
              <UnitsDropdown
                defaultValue={unit || 'pieces'}
                isImperial={imperialForm}
                name={unitFieldName}
                options={optionsComponentsMetadata}
                setValue={setValue}
                unit={currentUnit || 'pieces'}
              />
            }
          />
        </div>
        <div className="sk:min-w-0 sk:flex-1 sk:px-4">
          <MaskedNumberFormField
            error={errors.carton_net_weight}
            required
            hint={errors.carton_net_weight?.message}
            name="carton_net_weight"
            label={<MuteText>Net Weight</MuteText>}
            helpText={`${tooltipTexts.cartonNetWeight} ${weightText}.`}
            placeholder="Net"
            control={control}
            defaultValue=""
            maskOptions={{ allowDecimal: true, decimalLimit }}
            inputRight={
              <UnitLabel
                className={cn(errors.carton_net_weight && 'sk:text-danger')}
              >
                {weightText}
              </UnitLabel>
            }
          />
        </div>
        <div className="sk:min-w-0 sk:flex-1 sk:px-4">
          <MaskedNumberFormField
            error={errors.carton_gross_weight}
            required
            hint={errors.carton_gross_weight?.message}
            name="carton_gross_weight"
            label={<MuteText>Gross Weight</MuteText>}
            placeholder="Gross"
            control={control}
            helpText={`${tooltipTexts.cartonGrossWeight} ${weightText}.`}
            defaultValue=""
            maskOptions={{ allowDecimal: true, decimalLimit }}
            inputRight={
              <UnitLabel
                className={cn(errors.carton_gross_weight && 'sk:text-danger')}
              >
                {weightText}
              </UnitLabel>
            }
          />
        </div>
      </div>
    </>
  );
}
